import React from "react";
import { Dropdown } from "antd";
import { TARGET_BASIS_COST, TARGET_BASIS_PRICE, costToBorrowerHint } from "../../pricingRequest";
import "./targetBasis.css";

// What a target price is compared with (11-04), picked in the form's Target
// field and in the results bar: the Price column (the default), or the Cost
// to borrower turned into a price (100 - cost / loan amount x 100). The box
// takes the same number either way; with Cost to borrower a line under it
// says what the number is in dollars at the current loan amount.

export const TARGET_BASIS_OPTIONS = [
  { value: TARGET_BASIS_PRICE, label: "Price" },
  { value: TARGET_BASIS_COST, label: "Cost to borrower" },
];

export const targetBasisLabel = (basis) => (basis === TARGET_BASIS_COST ? "Cost to borrower" : "Price");

// The tip next to the target price, for each basis.
export const targetPriceTip = (basis) => (basis === TARGET_BASIS_COST
  ? "Matched against each rate's Cost to borrower as a price: 100 − cost to borrower ÷ loan amount × 100. 100 is $0 cost to the borrower, 99 is 1% of the loan amount, above 100 is a credit to the borrower. The lowest rate at or above the target is the Target row."
  : "Matched against the Price column: lock-day price plus the pricing adjustments, compensation not included. 100 is no points and no credit; above 100 is a lender credit, below 100 is a cost to the borrower.");

const Chevron = () => (
  <svg className="ppe-basis-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// The button's short text where the name row is narrow (the results bar's
// Target, 168px at 1280 wide): "Cost to borrower" ran into the Compensation
// label next to it (11-04). The menu and the screen-reader name keep the
// full name.
const SHORT_LABEL = { [TARGET_BASIS_PRICE]: "Price", [TARGET_BASIS_COST]: "Cost" };

// A small "Price ▾" / "Cost to borrower ▾" menu button for a field's name
// row; `short` shows "Cost ▾" instead.
export function TargetBasisPicker({ id, value, onChange, short = false }) {
  const current = value === TARGET_BASIS_COST ? TARGET_BASIS_COST : TARGET_BASIS_PRICE;
  const label = targetBasisLabel(current);
  const shown = short ? SHORT_LABEL[current] : label;
  return (
    <Dropdown
      trigger={["click"]}
      menu={{
        items: TARGET_BASIS_OPTIONS.map((option) => ({ key: option.value, label: option.label })),
        selectable: true,
        selectedKeys: [current],
        onClick: ({ key }) => {
          if (key !== current) onChange(key);
        },
      }}
    >
      <button
        type="button"
        id={id}
        className="ppe-basis-button"
        title={`Target price matched against: ${label}`}
        aria-label={`Target price matched against: ${label}`}
      >
        <span className="ppe-basis-text">{shown}</span>
        <Chevron />
      </button>
    </Dropdown>
  );
}

// "≈ $4,000 cost to borrower" under the box, only with Cost to borrower.
export function TargetBasisHint({ id, basis, price, loanAmount }) {
  if (basis !== TARGET_BASIS_COST) return null;
  const text = costToBorrowerHint(price, loanAmount);
  if (!text) return null;
  return <div id={id} className="ppe-target-hint">{text}</div>;
}
